import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import type { EditBrandPath } from "~/pages/administration/brand-setting-page/brand/brand-paths";
import type { Brand } from "~/types/api";

export type EditBrand = Brand & { brandPaths: EditBrandPath[] };

type BrandsMutationContext = {
  previousBrands: Brand[] | undefined;
};

export const useUpdateBrandPaths = () => {
  const queryClient = useQueryClient();
  const brandsQueryOptions = configurationApi.queryOptions("get", "/api/Brand");
  const brandsQueryKey = brandsQueryOptions.queryKey;

  const brands = queryClient.getQueryData<Brand[]>(brandsQueryKey);
  const { showToast } = useToast();

  const { mutate, isSuccess, isError, isPending } = configurationApi.useMutation(
    "put",
    "/api/Brand/{brandId}/paths",
    {
      onMutate: async (variables) => {
        const brandIdToUpdate = variables.params.path.brandId;
        const newPaths = variables.body.paths;

        await queryClient.cancelQueries({ queryKey: brandsQueryKey });
        const previousBrands = queryClient.getQueryData<Brand[]>(brandsQueryKey);

        queryClient.setQueryData<EditBrand[]>(brandsQueryKey, (oldBrands) => {
          if (!oldBrands) return [];

          return oldBrands.map((brand) => {
            if (brand.id !== brandIdToUpdate) {
              return brand;
            }
            return {
              ...brand,
              brandPaths: newPaths,
            };
          });
        });
        return { previousBrands } as BrandsMutationContext;
      },

      onError: (_err, _variables, context) => {
        const previousBrands = (context as BrandsMutationContext)?.previousBrands;
        showToast("error", "Failed to update brand paths. Rolling back.");
        if (previousBrands) {
          queryClient.setQueryData(brandsQueryKey, previousBrands);
        }
      },

      onSuccess: () => {
        showToast("success", "Brand paths updated successfully.");
      },

      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: brandsQueryKey });
      },
    }
  );

  const updateBrandPath = (
    mode: "create" | "edit" | "delete",
    pathIndex?: number,
    brandId?: number,
    path?: string,
    includeSubFolders?: boolean
  ) => {
    if (!brandId || !path) return;

    const currentPaths = brands?.find((b) => b.id === brandId)?.brandPaths ?? [];
    const pathEntry = { path, includeSubFolders: !!includeSubFolders };

    let updatedPaths = currentPaths;

    if (mode === "create") {
      updatedPaths = [...currentPaths, pathEntry];
    } else if (mode === "edit") {
      if (pathIndex === undefined) return;
      updatedPaths = currentPaths.map((p, i) => (i === pathIndex ? pathEntry : p));
    } else if (mode === "delete") {
      if (pathIndex === undefined) return;
      updatedPaths = currentPaths.filter((_, i) => i !== pathIndex);
    }

    mutate({
      body: { paths: updatedPaths },
      params: { path: { brandId } },
    });
  };

  return { updateBrandPath, isSuccess, isError, isPending };
};
